import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { finalize, Subject, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { Dayjs } from 'dayjs';
import { CoachCalendarService } from '../../../core/services/coach-calendar.service';
import { CoachQuickNoteService } from '../../../core/services/coach-quick-note.service';
import { IAppointment, IRescheduleRequest } from '../../../core/models/appointment.model';
import { EAppointmentStatus } from '../../../core/enums/appointment-status.enum';
import { isSameDay, tz } from '../../../core/helpers/calendar.helper';

/** How a meeting reads on the daily list. */
type TAppointmentState = 'completed' | 'next' | 'upcoming' | 'past';

interface IQuickNote {
  id: number;
  content: string;
  createdAt: string;
}

@Component({
  selector: 'coach-agenda-today',
  templateUrl: './agenda-today.component.html',
  styleUrl: './agenda-today.component.scss',
})
export class AgendaTodayComponent implements OnInit, OnChanges, OnDestroy {
  /** Loaded by the agenda shell; only the pending counter needs it. */
  @Input() rescheduleRequests: IRescheduleRequest[] = [];
  /** Changes whenever the shell answered a reschedule request and a meeting moved. */
  @Input() refreshToken = 0;

  today: Dayjs = tz();
  todayAppointments: IAppointment[] = [];
  yesterdayOpen: IAppointment[] = [];
  tomorrowAppointments: IAppointment[] = [];
  quickNotes: IQuickNote[] = [];

  fgNote: FormGroup = this.formBuilder.group({
    content: [null, [Validators.required, Validators.maxLength(2000)]],
  });

  isLoading = false;
  noteIsLoading = false;
  notesIsLoading = true;
  noteIsSubmitted = false;

  destroyed$ = new Subject();

  constructor(
    private formBuilder: FormBuilder,
    private calendarService: CoachCalendarService,
    private quickNoteService: CoachQuickNoteService,
    private router: Router,
    private toastrService: ToastrService,
    private translate: TranslateService,
  ) {
  }

  ngOnInit(): void {
    this.getAppointments();
    this.getQuickNotes();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['refreshToken'] && !changes['refreshToken'].firstChange) {
      this.getAppointments();
    }
  }

  completedCount(): number {
    return this.todayAppointments.filter(a => a.status === EAppointmentStatus.Completed).length;
  }

  totalCount(): number {
    return this.todayAppointments.length;
  }

  progressPercent(): number {
    return this.totalCount() ? Math.round((this.completedCount() / this.totalCount()) * 100) : 0;
  }

  /**
   * The daily range covers yesterday and tomorrow as well, because the closing section reports on
   * both. Reusing the week range would miss them on a Monday or a Sunday.
   */
  getAppointments() {
    const from = this.today.subtract(1, 'day').startOf('day');
    const to = this.today.add(1, 'day').endOf('day');

    this.isLoading = true;
    this.calendarService.getAppointments(from.toDate(), to.toDate())
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isLoading = false))
      .subscribe(r => {
        const appointments = (r.data as IAppointment[])
          .filter(a => a.status !== EAppointmentStatus.Cancelled)
          .sort((a, b) => tz(a.startsAt).valueOf() - tz(b.startsAt).valueOf());

        const yesterday = this.today.subtract(1, 'day');
        const tomorrow = this.today.add(1, 'day');

        this.todayAppointments = appointments.filter(a => isSameDay(tz(a.startsAt), this.today));
        this.tomorrowAppointments = appointments.filter(a => isSameDay(tz(a.startsAt), tomorrow));
        this.yesterdayOpen = appointments.filter(a =>
          isSameDay(tz(a.startsAt), yesterday) && (a.status !== EAppointmentStatus.Completed));
      });
  }

  getQuickNotes() {
    this.notesIsLoading = true;
    this.quickNoteService.getNotes('?page=1&size=3')
      .pipe(takeUntil(this.destroyed$), finalize(() => this.notesIsLoading = false))
      .subscribe(r => {
        this.quickNotes = (r.data?.result ?? r.data) as IQuickNote[];
      });
  }

  /** The first meeting still ahead today is the one the coach is about to join. */
  nextAppointment(): IAppointment | undefined {
    const now = tz();
    return this.todayAppointments.find(a =>
      (a.status !== EAppointmentStatus.Completed) && tz(a.startsAt).isAfter(now));
  }

  stateOf(appointment: IAppointment): TAppointmentState {
    if (appointment.status === EAppointmentStatus.Completed) {
      return 'completed';
    }
    if (this.nextAppointment()?.id === appointment.id) {
      return 'next';
    }

    return tz(appointment.startsAt).isAfter(tz()) ? 'upcoming' : 'past';
  }

  durationMinutes(appointment: IAppointment): number {
    return Math.round(tz(appointment.endsAt).diff(tz(appointment.startsAt), 'minute', true));
  }


  levelKey(appointment: IAppointment): string {
    return appointment.student?.userInfo?.level?.key ?? 'default';
  }

  openMeetLink(appointment: IAppointment) {
    if (appointment.meetLink) {
      window.open(appointment.meetLink, '_blank');
    }
  }

  openStudent(studentId?: number) {
    if (studentId) {
      this.router.navigateByUrl(`/dashboard/students/${studentId}`);
    }
  }

  saveNote() {
    this.noteIsSubmitted = true;
    if (this.fgNote.invalid) {
      return;
    }

    this.noteIsLoading = true;
    this.quickNoteService.createNote(this.fgNote.value.content)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.noteIsLoading = false))
      .subscribe(() => {
        this.noteIsSubmitted = false;
        this.fgNote.patchValue({content: null});
        this.toastrService.success(this.translate.instant('agenda.daily.note_saved'));
        this.getQuickNotes();
      });
  }

  deleteNote(note: IQuickNote) {
    this.quickNoteService.deleteNote(note.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.toastrService.success(this.translate.instant('agenda.daily.note_deleted'));
        this.getQuickNotes();
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
